import type { ReactElement } from "react";
import { ArticleHero } from "../ArticleHero";
import babyHeroImage from "../../assets/images/baby-outdoor-fall-portrait.webp";
import babyStudioImage from "../../assets/images/baby-first-birthday-bakery-themed-portrait.webp";
import babyStudioImage6 from "../../assets/images/baby-first-birthday-strawberry-studio-portrait.webp";
import { ArticlePhoto } from "./ArticlePhoto";
import { UiIcon } from "./components/UiIcons";
import { GuideCtaActions } from "./components/GuideCtaActions";
import "./NewbornPages.scss";
import "./NewbornArticle.scss";
import "./BabyArticle.scss";

const flow = [
  { number: "01", title: "Settle in", text: "We give your baby time to look around and feel safe." },
  { number: "02", title: "Connect", text: "We begin with you, familiar voices and gentle play." },
  { number: "03", title: "Explore", text: "Portraits follow your baby’s current skills and curiosity." },
  { number: "04", title: "Pause", text: "Feeding, cuddles and resets happen whenever needed." },
];

export const WhatHappensDuringBabySession = (): ReactElement => (
  <main className="newborn-page newborn-article-page baby-article-page">
    <article>
      <ArticleHero
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Resources", to: "/resources/" },
          { label: "Baby", to: "/resources/baby/" },
          { label: "What Happens During a Baby Photography Session?" },
        ]}
        category="Baby session guide"
        description="A relaxed, play-led session built around your baby’s personality, attention and current milestones."
        imageAlt="Happy baby interacting naturally during a photography session"
        imagePosition="center 45%"
        imageSrc={babyHeroImage}
        readingTime="6 min read"
        title="What Happens During a Baby Photography Session?"
        updatedAt="2026-08-04"
        updatedLabel="Updated August 2026"
      />
      <div className="newborn-shell newborn-article-layout">
        <aside className="newborn-article-callout">
          <span className="newborn-article-callout__icon">
            <UiIcon name="star" />
          </span>
          <div>
            <h2>Quick answer</h2>
            <p>
              A baby session moves between connection, play, simple portraits and breaks. There is no expectation that
              your baby smile on command or stay still. We follow their attention and use familiar people, sounds and
              movement to create natural expressions.
            </p>
          </div>
        </aside>
        <nav aria-label="In this guide" className="newborn-article-toc">
          <h2>In this guide</h2>
          <ol>
            <li>
              <a href="#flow">Session flow</a>
            </li>
            <li>
              <a href="#begin">How we begin</a>
            </li>
            <li>
              <a href="#portraits">Portrait variety</a>
            </li>
            <li>
              <a href="#breaks">Breaks and feeding</a>
            </li>
            <li>
              <a href="#family">Family photographs</a>
            </li>
            <li>
              <a href="#shy">If baby is shy</a>
            </li>
          </ol>
        </nav>
        <section className="newborn-article-section" id="flow">
          <p className="newborn-article-kicker">A FLEXIBLE RHYTHM</p>
          <h2>Baby Session Flow</h2>
          <p>
            The order can change at any time. We use the happiest, most curious moments first and save quiet connection
            for whenever your baby needs to slow down.
          </p>
          <div className="baby-session-flow">
            {flow.map((step) => (
              <article key={step.number}>
                <span>{step.number}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="newborn-article-section newborn-article-split" id="begin">
          <div>
            <p className="newborn-article-kicker">THE FIRST FEW MINUTES</p>
            <h2>We begin without rushing</h2>
            <p>
              Your baby may need time to study the room, photographer or outdoor surroundings. We start close to a
              parent and let curiosity build naturally rather than trying immediately for a smile.
            </p>
            <p>
              A familiar song, quiet game or favorite voice usually works better than a room full of toys and noise.
            </p>
          </div>
          <ArticlePhoto
            alt="Baby posing in a bakery-themed first birthday studio set"
            className="newborn-article-split__photo"
            label="PHOTO: SETTLING IN"
            src={babyStudioImage}
          />
        </section>
        <section className="newborn-article-section" id="portraits">
          <p className="newborn-article-kicker">LED BY MILESTONES</p>
          <h2>What kinds of portraits we create</h2>
          <p>
            Depending on age, we may photograph tummy time, sitting, crawling, standing with support or walking.
            Close-up details, serious expressions and moments of concentration belong in the gallery alongside smiles.
          </p>
        </section>
        <section className="newborn-article-section" id="breaks">
          <p className="newborn-article-kicker">BREAKS ARE PART OF THE PLAN</p>
          <h2>Feeding, changing and resetting</h2>
          <p>
            If your baby becomes hungry, tired or overwhelmed, we stop. A snack, feed, diaper change or cuddle can
            restore the mood much faster than pushing for one more photograph.
          </p>
          <aside className="newborn-article-callout newborn-article-callout--tip">
            <span className="newborn-article-callout__icon">
              <UiIcon name="tip" />
            </span>
            <div>
              <h3>No performance required</h3>
              <p>
                A thoughtful or serious baby is not having a bad session. Their real expressions are part of who they
                are today.
              </p>
            </div>
          </aside>
        </section>
        <section className="newborn-article-section newborn-article-split newborn-article-split--reverse" id="family">
          <ArticlePhoto
            alt="Smiling baby celebrating a first birthday in a strawberry studio setup"
            className="newborn-article-split__photo"
            label="PHOTO: FAMILY CONNECTION"
            src={babyStudioImage6}
          />
          <div>
            <p className="newborn-article-kicker">THE PEOPLE THEY LOVE</p>
            <h2>Family photographs feel like interaction</h2>
            <p>
              Rather than asking everyone to look at the camera for every image, I guide cuddling, lifting, walking and
              simple games. These actions help your baby relax and create photographs full of genuine connection.
            </p>
          </div>
        </section>
        <section className="newborn-article-section" id="shy">
          <p className="newborn-article-kicker">A SLOWER APPROACH</p>
          <h2>What if your baby is shy or unsettled?</h2>
          <p>
            We reduce stimulation, keep your baby close to you and allow more observation time. The session does not
            depend on constant smiles. Quiet eye contact, hands holding yours and gradual curiosity can make especially
            meaningful photographs.
          </p>
        </section>
        <section className="newborn-article-cta baby-article-cta">
          <div>
            <p className="newborn-article-kicker">BUILT AROUND YOUR BABY</p>
            <h2>Wondering what a session would look like at this age?</h2>
            <p>Explore the baby packages and pricing, or contact me if you need help shaping the session around this age.</p>
          </div>
          <GuideCtaActions category="baby" />
        </section>
      </div>
    </article>
  </main>
);
export default WhatHappensDuringBabySession;
